import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  FiCalendar, FiFilter, FiSearch, FiRefreshCw, 
  FiCheckCircle, FiXCircle, FiClock, FiFileText, 
  FiCheck, FiX, FiAlertTriangle, FiExternalLink, FiArrowLeft
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import ConfirmModal from './shared/ConfirmModal';
import { fmtDate } from '../utils/istDate';

const api = axios.create({ baseURL: process.env.REACT_APP_BASE_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const STATUS_UI = {
  APPROVED: { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: FiCheckCircle },
  PENDING: { color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', icon: FiClock },
  REJECTED: { color: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200', icon: FiXCircle },
  CANCELLED: { color: 'text-slate-600', bg: 'bg-slate-100', border: 'border-slate-200', icon: FiXCircle },
};

const LEAVE_TYPES = ['ALL', 'ANNUAL', 'SICK', 'CASUAL', 'MATERNITY', 'PATERNITY'];

export default function HrAllLeaves() {
  const navigate = useNavigate();
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('ALL');
  const [selectedType, setSelectedType] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [leaveBalances, setLeaveBalances] = useState({});
  const [actionRemark, setActionRemark] = useState('');
  const [confirm, setConfirm] = useState({ open: false, leave: null, status: null, loading: false });

  const fetchLeaveBalance = async (employeeId) => {
    if (!employeeId || leaveBalances[employeeId]) return;
    try {
      const year = new Date().getFullYear();
      const response = await api.get(`/leave/balance/${employeeId}/${year}`);
      setLeaveBalances((prev) => ({ ...prev, [employeeId]: response.data }));
    } catch (err) {
      console.error("Balance Error:", err);
    }
  };

  const fetchAllLeaves = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      let response;
      if (selectedStatus !== 'ALL') {
        response = await api.get(`/leave/status/${selectedStatus}`);
      } else {
        response = await api.get('/leave/all');
      }

      const raw = response.data;
      let list = Array.isArray(raw) 
        ? raw 
        : Array.isArray(raw?.content) 
        ? raw.content 
        : Array.isArray(raw?.data) 
        ? raw.data 
        : [];
        
      if (selectedType !== 'ALL') {
        list = list.filter((leave) => leave.leaveType === selectedType);
      }

      setLeaves(list);
      const uniqueEmpIds = [...new Set(list.map((leave) => leave.employeeId).filter(Boolean))];
      uniqueEmpIds.forEach((empId) => {
        fetchLeaveBalance(empId);
      });
    } catch (err) {
      console.error(err);
      setError('Failed to fetch leave records');
    } finally {
      setLoading(false);
    }
  }, [selectedStatus, selectedType]);

  useEffect(() => {
    fetchAllLeaves();
  }, [fetchAllLeaves]);

  const openConfirm = (leave, status) => {
    setActionRemark('');
    setConfirm({ open: true, leave, status, loading: false });
  };

  const handleLeaveAction = async () => {
    const { leave, status } = confirm;
    if (!leave) return;

    setConfirm(prev => ({ ...prev, loading: true }));
    try {
      const remarks = actionRemark.trim() || (status === 'APPROVED' ? 'Approved by HR' : 'Rejected by HR');
      await api.put('/leave/approve', { leaveId: leave.id, status, remarks });
      
      toast.success(`Leave request ${status.toLowerCase()} successfully.`);
      setConfirm({ open: false, leave: null, status: null, loading: false });
      setActionRemark('');
      fetchAllLeaves();
    } catch (err) {
      console.error(err);
      toast.error(`Failed to ${status.toLowerCase()} leave request`);
      setConfirm(prev => ({ ...prev, loading: false }));
    }
  };

  const filteredLeaves = leaves.filter(leave => {
    const name = (leave.employeeName || leave.employeeUsername || '').toLowerCase();
    const type = (leave.leaveType || '').toLowerCase();
    const reason = (leave.reason || '').toLowerCase();
    const query = searchTerm.toLowerCase();
    
    const matchesSearch = name.includes(query) || type.includes(query) || reason.includes(query) || String(leave.employeeId || '').includes(query);
    const matchesStatus = selectedStatus === 'ALL' || leave.status === selectedStatus;
    const matchesType = selectedType === 'ALL' || leave.leaveType === selectedType;

    return matchesSearch && matchesStatus && matchesType;
  });

  const totalCount = leaves.length;
  const approvedCount = leaves.filter(l => l.status === 'APPROVED').length;
  const pendingCount = leaves.filter(l => l.status === 'PENDING').length;
  const rejectedCount = leaves.filter(l => l.status === 'REJECTED' || l.status === 'CANCELLED').length;

  return (
    <div className="animate-fade-in space-y-6 pb-12">
      <ConfirmModal
        isOpen={confirm.open}
        onClose={() => setConfirm({ open: false, leave: null, status: null, loading: false })}
        onConfirm={handleLeaveAction}
        loading={confirm.loading}
        title={confirm.status === 'APPROVED' ? 'Approve Leave Request?' : 'Reject Leave Request?'}
        message={
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              {confirm.status === 'APPROVED'
                ? `Are you sure you want to approve the leave request for ${confirm.leave?.employeeName || confirm.leave?.employeeUsername || 'this employee'}?`
                : `Are you sure you want to reject the leave request for ${confirm.leave?.employeeName || confirm.leave?.employeeUsername || 'this employee'}?`}
            </p>
            <div className="pt-2">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Remarks (Optional)</label>
              <input
                type="text"
                placeholder="Enter remarks..."
                value={actionRemark}
                onChange={(e) => setActionRemark(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
          </div>
        }
        confirmText={confirm.status === 'APPROVED' ? 'Approve' : 'Reject'}
        variant={confirm.status === 'APPROVED' ? 'info' : 'danger'}
      />

      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <button
            type="button"
            onClick={() => navigate('/hr/leave-approvals')}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-blue-600 transition-colors mb-2 cursor-pointer group"
          >
            <FiArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
            <span>Back to Leave Approvals</span>
          </button>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight">All Employee Leave Directory</h1>
          <p className="text-sm text-slate-500 font-medium mt-1">Complete record of leave applications across all departments</p>
        </div>

        <button
          onClick={fetchAllLeaves}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 font-bold rounded-xl text-xs transition-colors shadow-xs"
        >
          <FiRefreshCw className={loading ? 'animate-spin text-blue-600' : ''} /> Refresh Data
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Total Applications</p>
          <p className="text-2xl font-black text-slate-800 mt-1">{totalCount}</p>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-emerald-200 shadow-sm bg-emerald-50/30">
          <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">Approved</p>
          <p className="text-2xl font-black text-emerald-800 mt-1">{approvedCount}</p>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-amber-200 shadow-sm bg-amber-50/30">
          <p className="text-xs font-bold uppercase tracking-wider text-amber-700">Pending</p>
          <p className="text-2xl font-black text-amber-800 mt-1">{pendingCount}</p>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-rose-200 shadow-sm bg-rose-50/30">
          <p className="text-xs font-bold uppercase tracking-wider text-rose-700">Rejected / Cancelled</p>
          <p className="text-2xl font-black text-rose-800 mt-1">{rejectedCount}</p>
        </div>
      </div>

      {/* Table & Filter Section */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
        {/* Filters bar */}
        <div className="p-5 border-b border-slate-100 bg-slate-50/60 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="relative flex-1 w-full md:max-w-md">
            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            <input
              type="text"
              placeholder="Search by employee name, ID, or leave type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            {/* Status Filter */}
            <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-medium">
              <FiFilter className="text-slate-400" />
              <span className="text-slate-500 font-semibold">Status:</span>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="bg-transparent font-bold text-slate-800 outline-none cursor-pointer"
              >
                <option value="ALL">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
              </select>
            </div>

            {/* Type Filter */}
            <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-medium">
              <span className="text-slate-500 font-semibold">Type:</span>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="bg-transparent font-bold text-slate-800 outline-none cursor-pointer"
              >
                {LEAVE_TYPES.map(t => (
                  <option key={t} value={t}>{t === 'ALL' ? 'All Types' : t}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-slate-600">
            <thead className="bg-slate-50/50 border-b border-slate-200 text-slate-500 text-[10px] uppercase tracking-widest font-bold">
              <tr>
                <th className="px-6 py-3.5">Employee</th>
                <th className="px-6 py-3.5">Leave Details</th>
                <th className="px-6 py-3.5 text-center">Duration</th>
                <th className="px-6 py-3.5 text-center">Paid / LOP</th>
                <th className="px-6 py-3.5">Status</th>
                <th className="px-6 py-3.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={6} className="py-12 text-center text-slate-500 font-medium">Loading leave records...</td></tr>
              ) : error ? (
                <tr><td colSpan={6} className="py-12 text-center text-rose-600 font-medium">{error}</td></tr>
              ) : filteredLeaves.length === 0 ? (
                <tr><td colSpan={6} className="py-16 text-center">
                  <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3 border border-dashed border-slate-200">
                    <FiFileText className="text-xl text-slate-400" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-800 mb-1">No Leave Records Found</h3>
                  <p className="text-slate-500 text-xs">No records match the current filters or search query.</p>
                </td></tr>
              ) : (
                filteredLeaves.map((leave) => {
                  const displayName = leave.employeeName || leave.employeeUsername || `Employee #${leave.employeeId || ''}`;
                  const st = STATUS_UI[leave.status] || STATUS_UI.PENDING;
                  const Icon = st.icon;
                  const balance = leaveBalances[leave.employeeId];

                  return (
                    <tr key={leave.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-700 font-extrabold text-xs flex items-center justify-center border border-slate-200">
                            {displayName.slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-bold text-slate-800 text-sm">{displayName}</div>
                            {leave.employeeId && (
                              <div className="text-[11px] font-mono text-slate-400">#{leave.employeeId}</div>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-slate-800">{leave.leaveType || 'Annual'}</span>
                          {leave.isHalfDay && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                              Half Day ({leave.halfDaySession === 'SECOND_HALF' ? 'Second Half' : 'First Half'})
                            </span>
                          )}
                        </div>
                        <div className="text-xs font-medium text-slate-500 mt-0.5">
                          {fmtDate(leave.startDate)} {leave.startDate !== leave.endDate && <><span className="text-slate-400">→</span> {fmtDate(leave.endDate)}</>}
                        </div>
                        {leave.reason && (
                          <p className="text-[11px] text-slate-400 italic mt-1 line-clamp-1">"{leave.reason}"</p>
                        )}
                      </td>

                      <td className="px-6 py-4 text-center">
                        <span className="inline-block px-2.5 py-1 bg-slate-100 text-slate-700 font-bold rounded-lg border border-slate-200 text-xs">
                          {leave.daysCount != null ? leave.daysCount : (leave.isHalfDay ? 0.5 : '—')} <span className="text-[9px] font-bold text-slate-400 uppercase">{leave.isHalfDay ? 'day' : 'days'}</span>
                        </span>
                      </td>

                      <td className="px-6 py-4 text-center">
                        {(() => {
                          const paid = leave.paidDays || 0;
                          const unpaid = leave.unpaidDays || 0;
                          if (paid === 0 && unpaid === 0) return <span className="text-slate-400">—</span>;
                          return (
                            <div className="flex flex-col items-center justify-center gap-0.5 text-xs">
                              <span className="font-bold text-emerald-600">{paid} Paid</span>
                              {unpaid > 0 && <span className="font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded text-[10px]">{unpaid} LOP</span>}
                            </div>
                          );
                        })()}
                      </td>

                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider border ${st.bg} ${st.color} ${st.border}`}>
                          <Icon size={12} /> {leave.status}
                        </span>
                      </td>

                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate(`/hr/leave/${leave.id}`, { state: { leave } })}
                            className="p-1.5 text-slate-500 hover:text-sky-600 hover:bg-sky-50 rounded-lg transition-all border border-slate-200"
                            title="View Detailed Info"
                          >
                            <FiExternalLink size={13} />
                          </button>
                          {leave.status === 'PENDING' ? (
                            <>
                              <button
                                onClick={() => openConfirm(leave, 'APPROVED')}
                                className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition-all"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => openConfirm(leave, 'REJECTED')}
                                className="px-2.5 py-1 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-lg transition-all"
                              >
                                Reject
                              </button>
                            </>
                          ) : (
                            <span className="text-xs text-slate-400 font-medium">Processed</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
